import React, { useState, useEffect } from 'react';
import { PlayCircle, AlertCircle, ChevronDown, ChevronRight, Users, Trash2 } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';
import { api } from '../services/api';
import ScrollToTop from './ScrollToTop';
import type { RoundsTabProps, Round, EvaluatorProgress } from '../types';

const RoundsTab: React.FC<RoundsTabProps> = ({ rounds, userRole, assignedRoundIds, currentUserEmail, onNewRound, onToggleRoundStatus, onStartComparisons, onDeleteRound }) => {
  const isAdmin = userRole === 'admin';
  const [showAllRounds, setShowAllRounds] = useState(false);
  const [deleteConfirmRoundId, setDeleteConfirmRoundId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Expanded secondary rounds sections per parent round
  const [expandedChildren, setExpandedChildren] = useState<Set<number>>(new Set());
  // Per-evaluator progress per round (lazy-loaded)
  const [evaluatorProgress, setEvaluatorProgress] = useState<Map<number, EvaluatorProgress[]>>(new Map());
  const [expandedProgress, setExpandedProgress] = useState<Set<number>>(new Set());
  const [progressLoading, setProgressLoading] = useState<Set<number>>(new Set());

  const statusOrder: Record<string, number> = { active: 0, paused: 1, completed: 2 };

  // Group secondary rounds by parent for progress display under parent card
  const childRoundsByParent = new Map<number, Round[]>();
  rounds.filter(r => r.isSecondary && r.parentRoundID).forEach(r => {
    const children = childRoundsByParent.get(r.parentRoundID!) || [];
    children.push(r);
    childRoundsByParent.set(r.parentRoundID!, children);
  });
  // Lazy-loaded child rounds for FR users (fallback when children aren't in the rounds array)
  const [fetchedChildRounds, setFetchedChildRounds] = useState<Map<number, Round[]>>(new Map());

  // For FR rounds whose children aren't in the rounds array, fetch them lazily
  useEffect(() => {
    const frRoundsNeedingChildren = rounds.filter(r =>
      r.isFinalReviewer && !r.isSecondary && !childRoundsByParent.has(r.roundID) && !fetchedChildRounds.has(r.roundID)
    );
    if (frRoundsNeedingChildren.length === 0) return;
    frRoundsNeedingChildren.forEach(async (r) => {
      try {
        const children = await api.getChildRounds(r.roundID);
        if (children.length > 0) {
          setFetchedChildRounds(prev => new Map(prev).set(r.roundID, children));
        }
      } catch (err) {
        console.error('Failed to load child rounds for FR:', err);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds]);

  // Merge fetched child rounds into the childRoundsByParent map
  fetchedChildRounds.forEach((children, parentId) => {
    if (!childRoundsByParent.has(parentId)) {
      childRoundsByParent.set(parentId, children);
    }
  });

  // Secondary rounds assigned to the current user show as top-level cards
  // (so they can start comparisons from the card); others stay nested only
  const mySecondaryRounds = rounds.filter(r =>
    r.isSecondary && assignedRoundIds?.includes(r.roundID)
  );
  const parentRounds = [...rounds.filter(r => !r.isSecondary), ...mySecondaryRounds];

  // For admins: by default show only rounds they created or are assigned to
  const filteredRounds = isAdmin && !showAllRounds
    ? parentRounds.filter(r =>
        (currentUserEmail && r.createdBy === currentUserEmail) ||
        assignedRoundIds?.includes(r.roundID)
      )
    : parentRounds;

  // Returns true when the evaluator has finished all their assigned pairs for a round
  const evaluatorIsDone = (r: typeof rounds[0]) =>
    (r.comparisonMode === 'partial' || r.isGrouped) && (r.myPendingComparisons ?? -1) === 0;

  // Sort priority: 0 = assigned to me, 1 = created by me (not assigned), 2 = others
  const getRoundPriority = (r: typeof rounds[0]) => {
    if (assignedRoundIds?.includes(r.roundID)) return 0;
    if (currentUserEmail && r.createdBy === currentUserEmail) return 1;
    return 2;
  };

  const sortedRounds = [...filteredRounds].sort((a, b) => {
    const priorityDiff = getRoundPriority(a) - getRoundPriority(b);
    if (priorityDiff !== 0) return priorityDiff;
    // Then by status (active > paused > completed)
    const statusDiff = (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    if (statusDiff !== 0) return statusDiff;
    // Within the same status: rounds where the evaluator still has work come before ones they've completed
    const aDone = evaluatorIsDone(a) ? 1 : 0;
    const bDone = evaluatorIsDone(b) ? 1 : 0;
    if (aDone !== bDone) return aDone - bDone;
    // Newest round first within the same group
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
  });

  // Pre-load evaluator progress for grouped active rounds where user is creator or FR
  useEffect(() => {
    const loadProgress = async (roundId: number) => {
      if (evaluatorProgress.has(roundId)) return;
      try {
        const progress = await api.getEvaluatorProgress(roundId);
        setEvaluatorProgress(prev => new Map(prev).set(roundId, progress));
      } catch (err) {
        console.error('Failed to pre-load evaluator progress:', err);
      }
    };

    sortedRounds
      .filter(r => r.isGrouped && r.status !== 'completed' && (r.totalPairs ?? 0) > 0 &&
        ((isAdmin && currentUserEmail && r.createdBy === currentUserEmail) || r.isFinalReviewer))
      .forEach(r => loadProgress(r.roundID));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rounds]);

  // Toggle per-evaluator progress for a round (lazy-load from API, parent round only)
  const toggleEvaluatorProgress = async (roundId: number) => {
    if (expandedProgress.has(roundId)) {
      setExpandedProgress(prev => { const next = new Set(prev); next.delete(roundId); return next; });
      return;
    }
    setExpandedProgress(prev => new Set(prev).add(roundId));
    if (!evaluatorProgress.has(roundId)) {
      setProgressLoading(prev => new Set(prev).add(roundId));
      try {
        const progress = await api.getEvaluatorProgress(roundId);
        setEvaluatorProgress(prev => new Map(prev).set(roundId, progress));
      } catch (err) {
        console.error('Failed to load evaluator progress:', err);
      } finally {
        setProgressLoading(prev => { const next = new Set(prev); next.delete(roundId); return next; });
      }
    }
  };

  // Toggle expanded children section
  const toggleChildren = (parentId: number) => {
    setExpandedChildren(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const getProgressPercentage = (round: typeof rounds[0]) => {
    if (round.status === 'completed') return 100;
    let totalPossible: number;
    // Numerator: all handled pairs (choices + skipped/unknown), not just choices
    const handled = round.totalHandledComparisons ?? round.completedComparisons;
    if ((round.comparisonMode === 'partial' || round.isGrouped) && round.totalPairs) {
      // Partial / grouped mode: denominator is the actual number of pre-seeded pairs
      totalPossible = round.totalPairs;
    } else {
      // Full mode: denominator is evaluators × full round-robin
      const n = round.participantCount - (round.unknownParticipantCount || 0);
      totalPossible = round.evaluatorCount * (n * (n - 1)) / 2;
    }
    if (totalPossible === 0) return 0;
    return Math.min(100, Math.round((handled / totalPossible) * 100));
  };

  // Compute comparisons display for a round (reused in both mobile + desktop)
  const getComparisonsDisplay = (round: typeof rounds[0]) => {
    const isCreator = isAdmin && currentUserEmail && round.createdBy === currentUserEmail;
    const showOverall = isCreator || round.isFinalReviewer;
    if (showOverall) {
      const children = childRoundsByParent.get(round.roundID) || [];
      const handled = (round.totalHandledComparisons ?? round.completedComparisons ?? 0)
        + children.reduce((s, c) => s + (c.totalHandledComparisons ?? c.completedComparisons ?? 0), 0);
      const total = (round.totalPairs ?? 0)
        + children.reduce((s, c) => s + (c.totalPairs ?? 0), 0);
      return { handled, total: (round.comparisonMode === 'partial' || round.isGrouped) && total ? total : 0 };
    } else {
      const myTotal = round.myTotalPairs ?? 0;
      const myCompleted = myTotal - (round.myPendingComparisons ?? 0);
      return { handled: myCompleted, total: myTotal > 0 ? myTotal : 0 };
    }
  };

  // Compute progress percentage for display (reused in both mobile + desktop)
  const getDisplayProgress = (round: typeof rounds[0]) => {
    const isCreator = isAdmin && currentUserEmail && round.createdBy === currentUserEmail;
    const showOverall = isCreator || round.isFinalReviewer;
    if (showOverall) {
      const children = childRoundsByParent.get(round.roundID) || [];
      const parentHandled = round.totalHandledComparisons ?? round.completedComparisons ?? 0;
      const parentTotal = (round.comparisonMode === 'partial' || round.isGrouped) && round.totalPairs
        ? round.totalPairs
        : (() => { const n = round.participantCount - (round.unknownParticipantCount || 0); return round.evaluatorCount * (n * (n - 1)) / 2; })();
      const childHandled = children.reduce((sum, c) => sum + (c.totalHandledComparisons ?? c.completedComparisons ?? 0), 0);
      const childTotal = children.reduce((sum, c) => {
        if ((c.comparisonMode === 'partial' || c.isGrouped) && c.totalPairs) return sum + c.totalPairs;
        const n = c.participantCount - (c.unknownParticipantCount || 0);
        return sum + c.evaluatorCount * (n * (n - 1)) / 2;
      }, 0);
      const totalHandled = parentHandled + childHandled;
      const totalPossible = parentTotal + childTotal;
      const pct = round.status === 'completed' && children.every(c => c.status === 'completed')
        ? 100
        : totalPossible === 0 ? 0 : Math.min(100, Math.round((totalHandled / totalPossible) * 100));
      return { pct, label: 'Round' };
    } else {
      const myTotal = round.myTotalPairs ?? 0;
      const myCompleted = myTotal - (round.myPendingComparisons ?? 0);
      const pct = round.status === 'completed' ? 100
        : myTotal === 0 ? 0 : Math.min(100, Math.round((myCompleted / myTotal) * 100));
      return { pct, label: 'Yours' };
    }
  };

  // Check if "Start Comparisons" button should show for a round
  const canStartComparisons = (round: typeof rounds[0]) => {
    if (round.status !== 'active' || !onStartComparisons || !assignedRoundIds?.includes(round.roundID)) return false;
    if ((round.comparisonMode === 'partial' || round.isGrouped) && (round.myPendingComparisons ?? -1) === 0) return false;
    if (round.isFinalReviewer) {
      const totalHandled = round.totalHandledComparisons ?? round.completedComparisons ?? 0;
      const totalPairs = round.totalPairs ?? 0;
      const myTotal = round.myTotalPairs ?? 0;
      const myCompleted = myTotal - (round.myPendingComparisons ?? 0);
      const othersHandled = totalHandled - myCompleted;
      const othersTotal = totalPairs - myTotal;
      const children = childRoundsByParent.get(round.roundID) || [];
      const childrenDone = children.every(c => {
        const h = c.totalHandledComparisons ?? c.completedComparisons ?? 0;
        const t = c.totalPairs ?? 0;
        return t > 0 && h >= t;
      });
      const primariesDone = othersTotal > 0 && othersHandled >= othersTotal;
      if (!primariesDone || (children.length > 0 && !childrenDone)) return false;
    }
    return true;
  };

  const canDelete = (round: typeof rounds[0]) =>
    isAdmin && onDeleteRound && currentUserEmail && round.createdBy === currentUserEmail &&
    round.completedComparisons === 0 && (round.totalHandledComparisons ?? 0) === 0 &&
    !(childRoundsByParent.get(round.roundID) || []).some(c => c.completedComparisons > 0 || (c.totalHandledComparisons ?? 0) > 0);

  // Evaluator progress section (shared between mobile + desktop)
  const renderEvaluatorProgress = (round: typeof rounds[0]) => {
    if (!(round.isGrouped && round.evaluatorCount > 0 && round.status !== 'completed' && (round.totalPairs ?? 0) > 0 &&
      ((isAdmin && currentUserEmail && round.createdBy === currentUserEmail) || round.isFinalReviewer))) return null;

    return (
      <div className="mt-3 sm:mt-4">
        <button
          onClick={() => toggleEvaluatorProgress(round.roundID)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
        >
          {expandedProgress.has(round.roundID) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Users className="w-4 h-4" />
          Evaluator Progress ({(() => {
            const allEps = evaluatorProgress.get(round.roundID);
            if (allEps) return allEps.filter(ep => ep.progressPercent < 100).length;
            return round.evaluatorCount;
          })()})
        </button>
        {expandedProgress.has(round.roundID) && (
          <div className="mt-2 sm:mt-3 space-y-2 pl-2 sm:pl-6">
            {progressLoading.has(round.roundID) ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : (evaluatorProgress.get(round.roundID) || []).filter(ep => ep.progressPercent < 100).length === 0 ? (
              <p className="text-sm text-gray-500 italic">All evaluators have completed their comparisons</p>
            ) : (
              (evaluatorProgress.get(round.roundID) || []).filter(ep => ep.progressPercent < 100).map(ep => (
                <div key={ep.evaluatorId} className="flex items-center gap-2 sm:gap-3">
                  <span className="text-sm text-gray-700 font-medium w-24 sm:w-40 truncate flex-shrink-0" title={ep.evaluatorName}>{ep.evaluatorName}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${ep.progressPercent >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, ep.progressPercent)}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right flex-shrink-0 ${ep.progressPercent >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                    {Math.round(ep.progressPercent)}%
                  </span>
                  <span className="text-xs text-gray-500 w-12 sm:w-16 text-right flex-shrink-0">
                    {ep.completedComparisons}/{ep.totalComparisons}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Secondary evaluations section (shared between mobile + desktop)
  const renderSecondaryEvals = (round: typeof rounds[0]) => {
    if (!((isAdmin && currentUserEmail && round.createdBy === currentUserEmail) || round.isFinalReviewer) || !childRoundsByParent.has(round.roundID)) return null;
    const activeChildren = childRoundsByParent.get(round.roundID)!.filter(c => c.status === 'active');
    if (activeChildren.length === 0) return null;

    return (
      <div className="mt-3 sm:mt-4">
        <button
          onClick={() => toggleChildren(round.roundID)}
          className="flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors cursor-pointer"
        >
          {expandedChildren.has(round.roundID) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Users className="w-4 h-4" />
          Secondary Evaluations ({activeChildren.length})
        </button>
        {expandedChildren.has(round.roundID) && (
          <div className="mt-2 sm:mt-3 space-y-2 pl-2 sm:pl-6">
            {activeChildren.map(child => {
              const childPct = getProgressPercentage(child);
              const fullMatch = child.roundName.match(/- (.+)\s*→\s*(.+)$/);
              const legacyMatch = child.roundName.match(/- Secondary \((.+)\)$/);
              const primaryLabel = fullMatch ? fullMatch[1].trim() : '';
              const secLabel = fullMatch ? fullMatch[2].trim() : legacyMatch ? legacyMatch[1] : child.roundName;
              const displayName = primaryLabel ? `${secLabel} (for ${primaryLabel})` : secLabel;
              const completed = child.totalHandledComparisons ?? child.completedComparisons ?? 0;
              const total = child.totalPairs ?? child.totalComparisons ?? 0;
              return (
                <div key={child.roundID} className="flex items-center gap-2 sm:gap-3">
                  <span className="text-sm text-gray-700 font-medium w-24 sm:w-40 truncate flex-shrink-0" title={displayName}>{displayName}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${childPct >= 100 ? 'bg-green-500' : 'bg-purple-500'}`}
                      style={{ width: `${Math.min(100, childPct)}%` }}
                    />
                  </div>
                  <span className={`text-xs font-semibold w-10 text-right flex-shrink-0 ${childPct >= 100 ? 'text-green-600' : 'text-purple-600'}`}>
                    {Math.round(childPct)}%
                  </span>
                  <span className="text-xs text-gray-500 w-12 sm:w-16 text-right flex-shrink-0">
                    {completed}/{total}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Evaluation Rounds</h2>
          <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
            {sortedRounds.length}
          </span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          {isAdmin && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAllRounds}
                onChange={e => setShowAllRounds(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 cursor-pointer"
              />
              <span className="text-sm text-gray-600 font-medium">Show All Rounds</span>
            </label>
          )}
          {isAdmin && (
            <button
              onClick={onNewRound}
              className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium cursor-pointer min-h-[44px]"
            >
              <PlayCircle className="w-5 h-5" />
              New Round
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {sortedRounds.map(round => {
          const comparisons = getComparisonsDisplay(round);
          const progress = getDisplayProgress(round);
          const showStart = canStartComparisons(round);
          const showDelete = canDelete(round);
          const showActivate = isAdmin && round.status === 'paused' && assignedRoundIds?.includes(round.roundID);

          return (
            <div
              key={round.roundID}
              className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              {/* ===== MOBILE CARD (< md) ===== */}
              <div className="md:hidden p-4">
                {/* Row 1: Name + progress ring */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-bold text-gray-900 truncate" title={round.roundName}>{round.roundName}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          round.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : round.status === 'completed'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {round.status.charAt(0).toUpperCase() + round.status.slice(1)}
                      </span>
                      {round.isGrouped && round.status !== 'completed' && (() => {
                        const isFinalReview = round.phase === 'phase2' && !round.isSecondary;
                        return (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            isFinalReview ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {isFinalReview ? 'Phase 2' : 'Phase 1'}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Circular progress indicator */}
                  <div className="flex-shrink-0 relative w-14 h-14">
                    <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                      <circle cx="28" cy="28" r="24" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                      <circle
                        cx="28" cy="28" r="24" fill="none"
                        stroke={progress.pct === 100 ? '#16a34a' : progress.pct > 0 ? '#2563eb' : '#d1d5db'}
                        strokeWidth="4"
                        strokeDasharray={`${2 * Math.PI * 24}`}
                        strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress.pct / 100)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
                      progress.pct === 100 ? 'text-green-600' : progress.pct > 0 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      {progress.pct}%
                    </span>
                  </div>
                </div>

                {/* Row 2: Compact stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                  <span>{formatDate(round.startDate)}</span>
                  <span className="text-gray-300">|</span>
                  <span className="text-blue-600 font-semibold">
                    {comparisons.handled}{comparisons.total ? ` / ${comparisons.total}` : ''} <span className="text-gray-400 font-normal">comparisons</span>
                  </span>
                </div>

                {/* Questions hint (collapsed) */}
                {round.questions && round.questions.length > 0 ? (
                  <p className="text-xs text-gray-500 mb-3">
                    <span className="w-1.5 h-1.5 bg-blue-600 rounded-full inline-block mr-1.5 align-middle"></span>
                    {round.questions.length} question{round.questions.length !== 1 ? 's' : ''}
                  </p>
                ) : (
                  <p className="text-xs text-orange-600 mb-3 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Simplified mode
                  </p>
                )}

                {/* Action buttons — full width, stacked */}
                {(showStart || showActivate || showDelete) && (
                  <div className="flex flex-wrap gap-2">
                    {showStart && (
                      <button
                        onClick={() => onStartComparisons!(round.roundID)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all font-medium text-sm min-h-[44px]"
                      >
                        <PlayCircle className="w-4 h-4" />
                        {(round.myTotalPairs ?? 0) - (round.myPendingComparisons ?? 0) > 0 ? 'Resume' : 'Start'}
                      </button>
                    )}
                    {showActivate && (
                      <button
                        onClick={() => onToggleRoundStatus(round.roundID, round.status)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95 transition-all font-medium text-sm border border-gray-200 min-h-[44px]"
                      >
                        <PlayCircle className="w-4 h-4" />
                        Activate
                      </button>
                    )}
                    {showDelete && (
                      <button
                        onClick={() => setDeleteConfirmRoundId(round.roundID)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 active:scale-95 transition-all font-medium text-sm border border-red-200 min-h-[44px]"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}
                  </div>
                )}

                {/* Evaluator + secondary progress */}
                {renderEvaluatorProgress(round)}
                {renderSecondaryEvals(round)}
              </div>

              {/* ===== DESKTOP CARD (>= md) ===== */}
              <div className="hidden md:block p-6">
                {/* Header row */}
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <h3 className="text-xl font-bold text-gray-900 truncate max-w-md" title={round.roundName}>{round.roundName}</h3>
                    <span
                      className={`px-4 py-1 rounded-full text-sm font-semibold shadow-sm ${
                        round.status === 'active'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : round.status === 'completed'
                          ? 'bg-gray-100 text-gray-800 border border-gray-200'
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      }`}
                    >
                      {round.status.charAt(0).toUpperCase() + round.status.slice(1)}
                    </span>
                    {round.isGrouped && round.status !== 'completed' && (() => {
                      const isFinalReview = round.phase === 'phase2' && !round.isSecondary;
                      return (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
                            isFinalReview
                              ? 'bg-amber-100 text-amber-800 border border-amber-200'
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}
                        >
                          {isFinalReview ? 'Phase 2 - Final Review' : 'Phase 1 - Group Evaluation'}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {showDelete && (
                      <button
                        onClick={() => setDeleteConfirmRoundId(round.roundID)}
                        className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 active:scale-95 transition-all duration-150 font-medium text-sm border border-red-200 shadow-sm hover:shadow-md cursor-pointer"
                        title="Delete Round"
                      >
                        <Trash2 className="w-4 h-4 inline mr-1" />
                        Delete
                      </button>
                    )}
                    {showActivate && (
                      <button
                        onClick={() => onToggleRoundStatus(round.roundID, round.status)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:scale-95 transition-all duration-150 font-medium text-sm border border-gray-200 shadow-sm hover:shadow-md cursor-pointer min-h-[44px]"
                      >
                        <PlayCircle className="w-4 h-4 inline mr-2" />
                        Activate Round
                      </button>
                    )}
                    {showStart && (
                      <button
                        onClick={() => onStartComparisons!(round.roundID)}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 font-medium text-sm shadow-sm hover:shadow-md cursor-pointer min-h-[44px]"
                      >
                        <PlayCircle className="w-4 h-4 inline mr-2" />
                        {(round.myTotalPairs ?? 0) - (round.myPendingComparisons ?? 0) > 0 ? 'Resume Comparisons' : 'Start Comparisons'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Questions */}
                {round.questions && round.questions.length > 0 ? (
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                      Questions ({round.questions.length}):
                    </h4>
                    <ul className="space-y-2">
                      {round.questions.map((q, idx) => (
                        <li key={q.questionID} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span className="pt-0.5">{q.questionText}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      <span>This round uses <strong>simplified comparison mode</strong> without detailed questions</span>
                    </p>
                  </div>
                )}

                {/* Stats grid */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 font-medium">Start Date</p>
                    <p className="font-semibold text-gray-900 mt-1">{formatDate(round.startDate)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 font-medium">End Date</p>
                    <p className="font-semibold text-gray-900 mt-1">{round.endDate ? formatDate(round.endDate) : 'Ongoing'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 font-medium">Comparisons</p>
                    <p className="font-semibold text-blue-600 mt-1 text-lg">
                      {comparisons.handled}
                      {comparisons.total ? <span className="text-gray-400 text-sm font-normal"> / {comparisons.total}</span> : null}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-xs text-gray-600 font-medium">Progress</p>
                    <div className="mt-1">
                      <p className="text-xs text-gray-500 font-medium">{progress.label}</p>
                      <p className="font-semibold text-lg" style={{ color: progress.pct === 100 ? '#16a34a' : progress.pct > 0 ? '#2563eb' : '#6b7280' }}>
                        {progress.pct}%
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${progress.pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                          style={{ width: `${progress.pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Evaluator + secondary progress */}
                {renderEvaluatorProgress(round)}
                {renderSecondaryEvals(round)}
              </div>
            </div>
          );
        })}

        {/* Delete Confirmation Modal */}
        {deleteConfirmRoundId !== null && (() => {
          const roundToDelete = rounds.find(r => r.roundID === deleteConfirmRoundId);
          return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Round</h3>
                <p className="text-gray-600 mb-1">
                  Are you sure you want to delete <strong>{roundToDelete?.roundName}</strong>?
                </p>
                <p className="text-sm text-red-600 mb-6">
                  This action cannot be undone. All associated data (questions, evaluator assignments, etc.) will be permanently removed.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirmRoundId(null)}
                    disabled={deleting}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium text-sm cursor-pointer min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!onDeleteRound) return;
                      setDeleting(true);
                      try {
                        await onDeleteRound(deleteConfirmRoundId);
                        setDeleteConfirmRoundId(null);
                      } catch (err) {
                        console.error('Failed to delete round:', err);
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 active:scale-95 transition-all font-medium text-sm cursor-pointer disabled:opacity-50 min-h-[44px]"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {sortedRounds.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-8 sm:p-12 text-center border border-gray-100">
            <PlayCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Rounds Yet</h3>
            <p className="text-gray-600 mb-6">
              {isAdmin && !showAllRounds
                ? 'No rounds created by you or assigned to you. Check "Show All Rounds" to see rounds from other admins.'
                : isAdmin
                ? 'Get started by creating your first evaluation round'
                : 'No rounds have been assigned to you yet'}
            </p>
            {isAdmin && (
              <button
                onClick={onNewRound}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg font-medium cursor-pointer"
              >
                <PlayCircle className="w-5 h-5 inline mr-2" />
                Create First Round
              </button>
            )}
          </div>
        )}
      </div>
      <ScrollToTop />
    </div>
  );
};

export default RoundsTab;
